import classNames from "classnames";
import * as H from "history";
import { isEqual } from "lodash";
import * as React from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";

import { RouteRestrictionBoundary } from "@app/authorization";
import { CountyProjectContainer, CountyProjectContextProps, ProjectOptions, RegionFilterItem } from "@app/projects";
import { ProjectModule } from "@app/projects/literals";
import PendingProviderChangesSidePanelLink from "@app/projects/servicing-provider/PendingProviderChangesSidePanelLink";
import { Breadcrumbs, BreadcrumbTemplateEnum, ErrorBoundary, formatDateTime, Page, PageHeader, PageHeaderLeft, PageInner, PageSection } from "@app/shared";
import {
  IconToggle as SkeletonIconToggle,
  PageHeader as SkeletonHeader,
  PrimaryTable as SkeletonTable,
} from "@app/shared/skeleton-components";
import { preventDefaultMouseEvent, parseQuerystring } from "@app/shared/utility";
import { RoleRestricted, Roles, UserContext, UserContextProps, UserInfo } from "@app/user";
import { ViewableFieldsContainer } from "@app/viewable-fields";

import { QESAlert, QESAlertsContainer, QESButton } from "@app/shared/qes-standards";
import { Flip, ToastPosition, toast } from "react-toastify";
import { MaintenanceAlert } from "../../maintenance";
import { AccuracyGapAnalysisStatus, FilterType, ProjectPerspective, ProjectRunConfigurationStatus, ProjectRunConfigurationType } from "../enumerators";
import { translateProjectChangeReason } from "../projectChangeReason";
import ProjectRunConfiguration from "../projectRunConfiguration";
import { ProjectRunConfigurationResult } from "../projectRunConfigurationResult";
import RegionFilters from "../regionFilters";
import { Details as AccuracyModule } from "./accuracy";
import { Details as AdequacyModule } from "./adequacy/Details";
import { Charts, SkeletonCharts } from "./Charts";
import { ProviderMemberMapping as MappingModule } from "./maps/ProviderMemberMapping";
import { NetworkCoverageMap } from "./maps/NetworkCoverageMap";
import { Options } from "./Options";
import Publish from "./Publish";
import ResourceControls from "./ResourceControls";
import { SummaryRouter } from "./summary";
import { AccuracyGapAnalysisContext, AccuracyGapAnalysisContextProvider, AccuracyGapAnalysisUiProps } from "../accuracy-gap-analysis/AccuracyGapAnalysisContext";
import { AgaAlert } from "./adequacy/AgaAlert";
import PendingUnprocessedSpecialtyGroupExceptionsChangesSidePanelLink from "../servicing-provider/PendingUnprocessedSpecialtyGroupExceptionsChangesSidePanelLink";
import { timeDistanceCalculatorTypeDisplayMap } from "../timeDistanceCalculatorTypeDisplayMap";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Features } from "@app/feature-flags";
import { ClaimsActivityRouter } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { CountyGapsMap } from "./maps/CountyGapsMap";
import { ClaimsInsightsRouter } from "@app/projects/network-assessment/pci-dashboard/claims-insights";
import { FeatureFlagContext, FeatureFlagContextProps } from "@app/feature-flags/FeatureFlagContext";
import { getTimeString } from "./adequacy/";
import { TemplateService } from "@app/templates";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import ProviderHubLandingPage from "./provider-hub/ProviderHubLandingPage";
import { JSX } from "react";
import { NetworkIntegrityPage } from './network-integrity';

/** The props passed in via the route used to get here. */
interface CountyProjectOverviewRouteProps {
  projectId: string;
}

/** The combined props received by the project overview. */
type CountyProjectOverviewProps =
  RouteComponentProps<CountyProjectOverviewRouteProps> &
  UserContextProps &
  CountyProjectContextProps &
  AccuracyGapAnalysisUiProps &
  FeatureFlagContextProps;

interface CountyProjectOverviewState {
  activeProjectModule: ProjectModule;
  authorizedProjectModules: ProjectModule[];
  disableAgaRunBanner: boolean;
  filters: JSX.Element;
  showCharts: boolean;
  statusBanners: JSX.Element | JSX.Element[];
  showTrendChart: boolean;
}

const CountyProjectOverview: React.FC<RouteComponentProps<CountyProjectOverviewRouteProps>> = (routeProps: RouteComponentProps<CountyProjectOverviewRouteProps>) => {
  const projectId = Number(routeProps.match.params.projectId);
  return (
    <UserContext.Consumer>
      {(user) => {
        if (!user) {
          throw 403;
        }
        return (
          <ErrorBoundary user={user}>
            <RouteRestrictionBoundary user={user} {...routeProps}>
              <CountyProjectContainer projectId={projectId}>
                {(countyProjectContextProps) =>
                  <AccuracyGapAnalysisContextProvider project={countyProjectContextProps.project} projectRunConfigurationId={countyProjectContextProps.runConfiguration?.projectRunConfigurationId}
                    publishStatus={!!countyProjectContextProps.runConfiguration?.datePublished} runConfigurationstatus={countyProjectContextProps.runConfiguration?.status} userRoles={user.roles}>
                    <AccuracyGapAnalysisContext.Consumer>
                      {(agaProps: AccuracyGapAnalysisUiProps) => (
                        <FeatureFlagContext.Consumer>
                          {(featureFlagProps: FeatureFlagContextProps) => (
                            <CountyProjectOverviewContent user={user} {...countyProjectContextProps} {...routeProps} {...agaProps} {...featureFlagProps} />
                          )}
                        </FeatureFlagContext.Consumer>
                      )}
                    </AccuracyGapAnalysisContext.Consumer>
                  </AccuracyGapAnalysisContextProvider>
                }
              </CountyProjectContainer>
            </RouteRestrictionBoundary>
          </ErrorBoundary>
        )
      }}
    </UserContext.Consumer>
  );
};

export default CountyProjectOverview;

/** Renders an overview page for a single project. */
class CountyProjectOverviewContent extends React.Component<CountyProjectOverviewProps, CountyProjectOverviewState> {
  private templateService = new TemplateService();
  private claimsActivityModulePath = "claims-activity";
  private claimsInsightsModulePath = "claims-insights";
  private summaryModulePath = "summary";
  private mappingModulePath = "mapping";
  private providerHubModulePath = "provider-hub";
  private networkIntegrityModulePath = "network-integrity";

  /** The module/view (tab) we're currently working with. */
  private get module() {
    let module: ProjectModule = null;
    const path = this.props.location.pathname;

    if (path.includes("accuracy")) {
      module = "Accuracy";
    } else if (path.includes("adequacy")) {
      module = "Adequacy";
    } else if (path.includes(this.summaryModulePath)) {
      module = "Summary";
    } else if (path.includes(this.mappingModulePath)) {
      module = "Mapping";
    } else if (path.includes(this.claimsActivityModulePath)) {
      module = "Claims-Activity";
    } else if (path.includes(this.claimsInsightsModulePath)) {
      module = "Claims-Insights"
    } else if (path.includes(this.providerHubModulePath)) {
      module = "Provider-Hub"
    } else if (path.includes(this.networkIntegrityModulePath)) {
      module = "Network-Integrity";
    }

    return module;
  }

  private canRenderStatusToast = true;
  private statusContainerRef = React.createRef<HTMLDivElement>();
  private statusToastId: string | number;

  constructor(props: CountyProjectOverviewProps) {
    super(props);

    this.state = {
      activeProjectModule: this.module,
      authorizedProjectModules: ["Adequacy"], // at the moment, every project automatically has "adequacy" checked. therefore, we do not have to wait for the backend call to determine whether or not the user has the appropriate permissions granted for this tab
      disableAgaRunBanner: false,
      filters: null,
      showCharts: true,
      showTrendChart: true,
      statusBanners: null,
    };
  }

  public async componentDidMount() {
    window.addEventListener("scroll", this.renderStatusToast);
  }

  public async componentDidUpdate(prevProps: CountyProjectOverviewProps) {
    let newState: Partial<CountyProjectOverviewState> = {};

    if (!isEqual(prevProps.user, this.props.user) ||
      !isEqual(prevProps.project, this.props.project) ||
      !isEqual(prevProps.runConfiguration, this.props.runConfiguration)) {
      const newStatusBanners = await this.renderStatus(this.props.user, this.props.project.resource.isShared, this.props.project.isRunning, this.props.processProviderChanges, this.props.project, this.props.runConfiguration, this.props.runProject, this.props.requeueMarketAndProspectNCP, this.props.forceUpdateProject, this.props.project.isReadOnly, this.props.project.isEditRestricted);
      newState = {
        ...newState,
        statusBanners: newStatusBanners,
      };
    }

    if (!isEqual(prevProps.filters, this.props.filters) ||
      !isEqual(prevProps.history.location, this.props.history.location)) {
      const newFilters = this.renderFilters(this.props.filters, this.props.removeFilter);
      newState = {
        ...newState,
        filters: newFilters,
      };
    }

    if (this.props.runConfiguration) {
      //set the project module if it's not already set or fix it if it's on an invalid module
      if (this.state.activeProjectModule === null && this.props.runConfiguration) {
        if (this.props.runConfiguration.adequacyTemplateCollectionId) {
          this.setActiveProjectModule("Adequacy", true);
        } else {
          this.setActiveProjectModule("Accuracy", true);
        }
      } else if (this.state.activeProjectModule === "Adequacy" && !this.props.runConfiguration.adequacyTemplateCollectionId) {
        this.setActiveProjectModule("Accuracy", true);
      } else if ((this.state.activeProjectModule === "Accuracy" && !this.props.runConfiguration.accuracyTemplateCollectionId) || ((this.state.activeProjectModule === "Claims-Activity" || this.state.activeProjectModule === "Claims-Insights") && (!this.props.runConfiguration.showPciDashboard || (!this.props.runConfiguration.showPciData)))) {
        this.setActiveProjectModule("Adequacy", true);
      } else if (this.state.activeProjectModule === "Mapping") {
        if (this.props.enabledFeatureFlags.has(Features.PROVIDER_MEMBER_MAPS)) {
          // If this is an adequacy project and the time slice is not published,
          // or viewing opportunity counties, force the user back to the adequacy tab
          if (this.props.runConfiguration.adequacyTemplateCollectionId) {
            const { runConfiguration } = this.props;

            if (!runConfiguration.datePublished) {
              this.setActiveProjectModule("Adequacy", true);
            }
          } else if (this.props.runConfiguration.accuracyTemplateCollectionId) {
            // cannot navigate from accuracy to mapping
            this.setActiveProjectModule("Accuracy", true);
          }
        } else {
          if (this.props.runConfiguration.adequacyTemplateCollectionId) {
            this.setActiveProjectModule("Adequacy", true);
          } else if (this.props.runConfiguration.accuracyTemplateCollectionId) {
            this.setActiveProjectModule("Accuracy", true);
          }
        }
      } else if (this.state.activeProjectModule === "Provider-Hub") {
        if (this.props.runConfiguration.adequacyTemplateCollectionId && !this.props.enabledFeatureFlags.has(Features.PROVIDER_HUB)) {
          this.setActiveProjectModule("Adequacy", true);
        } else if (this.props.runConfiguration.accuracyTemplateCollectionId) {
          this.setActiveProjectModule("Accuracy", true);
        }
      }
    }

    if (Object.keys(newState).length) {
      this.setState((prevState) => ({
        ...prevState,
        ...newState,
      }), () => {
        if (newState.hasOwnProperty(nameof(newState.statusBanners))) {
          this.canRenderStatusToast = true;
        }
      });
    }

    // corrects the active project module in case the back button is used after switching between tabs
    const activeProjectModule: ProjectModule = this.module;
    if (activeProjectModule !== this.state.activeProjectModule) {
      this.setState({
        activeProjectModule,
      });
    }

    if (!isEqual(prevProps.runConfiguration?.accuracyGapAnalysisStatus, this.props.runConfiguration?.accuracyGapAnalysisStatus) &&
      this.props.runConfiguration?.accuracyGapAnalysisStatus == AccuracyGapAnalysisStatus.Failed || this.props.runConfiguration?.accuracyGapAnalysisStatus == AccuracyGapAnalysisStatus.Canceled)
      this.setState({
        disableAgaRunBanner: false,
      });
  }

  public componentWillUnmount() {
    window.removeEventListener("scroll", this.renderStatusToast);
    this.removeStatusToast();
  }

  public render() {
    const query = parseQuerystring(this.props.location.search);
    const showProjectOptions = query.showProjectOptions && query.showProjectOptions === "true" || false;

    return this.renderOverview(this.props.error, this.props.loading, this.props.project, this.props.runConfiguration, showProjectOptions, this.props.user, this.props.regionFilters, this.props.perspective);
  }

  public resetStatusBanners = async (hasUnprocessedTerminations: boolean, hasUnprocessedTargets: boolean) => {
    let newState: Partial<CountyProjectOverviewState> = {};
    if (this.props.project) {
      const updatedProject = this.props.project;
      updatedProject.hasUnprocessedProviderTerminations = hasUnprocessedTerminations;
      updatedProject.hasUnprocessedProjectionProviders = hasUnprocessedTargets;

      const newStatusBanners = await this.renderStatus(this.props.user, this.props.project.resource.isShared, this.props.project.isRunning, this.props.processProviderChanges, updatedProject, this.props.runConfiguration, this.props.runProject, this.props.requeueMarketAndProspectNCP, this.props.forceUpdateProject, this.props.project.isReadOnly, this.props.project.isEditRestricted);
      newState = {
        ...newState,
        statusBanners: newStatusBanners,
      };

      if (Object.keys(newState).length) {
        this.setState((prevState) => ({
          ...prevState,
          ...newState,
        }), () => {
          if (newState.hasOwnProperty(nameof(newState.statusBanners))) {
            this.canRenderStatusToast = true;
          }
        });
      }
    }
  }

  /* this function gets called on load after each */
  /* project module's authorization has been determined to be true */
  public handleAuthorizationUpdate = (permitLookup: string) => {
    if (permitLookup === "VIEW_ACCURACY_TAB") {
      this.setState((prevState) => ({
        ...prevState,
        authorizedProjectModules: prevState.authorizedProjectModules.concat("Accuracy"),
      }));
    }

    if (this.state.activeProjectModule === null) {
      // only resort to setting a default tab
      // if the url on load path does not contain a project module
      // this will not be triggered at the moment,
      // due to the fact that adequacy is currently automatically checked
      // and does not require a backend call to determine accessibility
      this.determineDefaultProjectModule();
    }
  }

  private removeStatusToast = () => {
    if (!!this.statusToastId) {
      this.canRenderStatusToast = false;
      toast.dismiss(this.statusToastId);
      this.statusToastId = undefined;
    }
  }

  private renderStatusToast = () => {
    if (this.statusContainerRef.current && this.statusContainerRef.current.querySelectorAll(".qes-alert").length > 0 && this.canRenderStatusToast) {
      const statusContainer = this.statusContainerRef.current;
      const offScreen = statusContainer.getBoundingClientRect().bottom < (16 * 3.5); // 3.5rem, the height of looper's nav bar
      const toastSettings = {
        autoClose: false as const, // TS said it expects "number | false" and a boolean wasn't acceptable, so...
        closeButton: true,
        closeOnClick: true,
        containerId: "z-index-1030",
        hideProgressBar: true,
        // The -110 is somewhat arbitrary, but it should scroll up over the project title
        onClick: () => window.scrollTo({ behavior: "smooth", top: statusContainer.getBoundingClientRect().top + document.documentElement.scrollTop - 110 }),
        position: "top-center" as ToastPosition,
        transition: Flip,

      };
      if (offScreen) {
        if (!this.statusToastId) {
          const childClasses = Array.from(statusContainer.querySelectorAll(".qes-alert")).reduce((result, child) => result += child.className, "");
          const toastString = "Click here to view project notifications.";
          if (childClasses.includes("danger")) {
            this.statusToastId = toast.error(toastString, toastSettings);
          } else if (childClasses.includes("warning")) {
            this.statusToastId = toast.warn(toastString, toastSettings);
          } else {
            this.statusToastId = toast.info(toastString, toastSettings);
          }
        }
      } else {
        this.removeStatusToast();
      }
    } else {
      this.removeStatusToast();
    }
  }

  /**
   * Takes the current path to produce a new path using @newModule.
   * Returns a path ready to be used in props.history.push.
   * @param newModule
   */
  private getNewPath = (location: H.Location, newModule: string, removeSearch = false): string => {
    const path = location.pathname;
    const oldPath = path.split("/");
    const oldModule = oldPath[3];
    let newPath: string;
    if (oldPath.length > 4 && this.state.activeProjectModule === null) {
      // if the url path contains "drilled in" parameters, but no project module,
      // remove everything to the right of where the project module should appear,
      // (checking to see if the entire path is valid is rather messy)
      // and resort to the default tab for the user
      newPath = oldPath[0] + "/" + oldPath[1] + "/" + oldPath[2] + "/" + newModule;
    } else {
      if (oldModule === undefined) {
        // if the url path does not include a project module
        // or any other parameter where the project module should be, add it
        const prefixedNewModule = "/" + newModule;
        newPath = path.concat(prefixedNewModule);
      } else {
        // insert the new module
        newPath = `${oldPath.slice(0, 3).join("/")}/${newModule}`;

        const shouldAppendParameters =
          oldPath.length > 4 &&
          !(oldModule === this.summaryModulePath || newModule === this.summaryModulePath || oldModule === this.claimsActivityModulePath || newModule === this.claimsActivityModulePath || oldModule === this.claimsInsightsModulePath || newModule === this.claimsInsightsModulePath) &&
          (!path.match(/inspection/i)) && // Inspection shares no commonality elsewhere, so ignore it on module nav.
          newModule != "provider-hub"; //When going to the ProviderHub tab, we should not append any parameter

        // To resolve issues when moving between maps and adequacy while using an Opportunity County
        const shouldAppendQueryString = newModule === "mapping";

        if (shouldAppendParameters) {
          //keep drilled in parameters if they exist when we switch between modules
          newPath += `/${oldPath.slice(4).join("/")}`;
        }

        if (shouldAppendQueryString) {
          newPath += location.search;
        }
      }
    }

    return newPath + (removeSearch ? "" : location.search);
  }

  private determineDefaultProjectModule = () => {
    // the priority order for default tabs is as follows:
    // adequacy > accuracy
    if (this.state.authorizedProjectModules.find((x) => x === "Adequacy")) {
      // default tab to adequacy
      this.setActiveProjectModule("Adequacy");
    } else if (this.state.authorizedProjectModules.find((x) => x === "Accuracy")) {
      // default tab to accuracy
      this.setActiveProjectModule("Accuracy");
    }
  }

  private renderOverview(
    error: JQuery.jqXHR,
    loading: boolean,
    project: ProjectOptions,
    runConfiguration: ProjectRunConfigurationResult,
    showProjectOptions: boolean,
    user: UserInfo,
    regionFilters: RegionFilters,
    perspective: ProjectPerspective,
  ) {

    const previewUser = user.roles.indexOf(Roles.PREVIEW) > -1;

    if (project && !error) {
      const isShared = project.resource.isShared;
      const isPublished = (runConfiguration && runConfiguration.datePublished ? true : false);
      const haveAccess = previewUser || (!isShared || (isShared && isPublished) ? true : false);
      const shouldShowAccuracyTab = runConfiguration && runConfiguration.accuracyTemplateId && (runConfiguration.status === ProjectRunConfigurationStatus.Succeeded);
      const shouldShowAdequacyTab = runConfiguration && runConfiguration.adequacyTemplateId && (runConfiguration.status === ProjectRunConfigurationStatus.Succeeded || runConfiguration.status === ProjectRunConfigurationStatus.FailedProviderChanges);
      const showSummary = shouldShowAdequacyTab && runConfiguration && (runConfiguration.status === ProjectRunConfigurationStatus.Succeeded || runConfiguration.status === ProjectRunConfigurationStatus.FailedProviderChanges) && (!regionFilters || !(regionFilters.state || regionFilters.county || regionFilters.specialty));
      const showMapping = ((shouldShowAdequacyTab
        && (this.props.enabledFeatureFlags.has(Features.PROVIDER_MEMBER_MAPS) || this.props.enabledFeatureFlags.has(Features.PROVIDER_MEMBER_MAPS_V2)))
        && (!this.props.enabledFeatureFlags.has(Features.PROVIDER_MEMBER_MAPS_V2) || runConfiguration.isLatestPublished)
        && (!!isPublished && !runConfiguration.isProjected && !runConfiguration.isProjectedTermed && !runConfiguration.isTermed)
        && (!this.props.isDualMembershipMode && runConfiguration.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental)
        && (!this.props.compareToRunConfiguration));
      const showPciData = runConfiguration?.status === ProjectRunConfigurationStatus.Succeeded && runConfiguration?.showPciData && runConfiguration?.showPciDashboard && !!runConfiguration.adequacyTemplateCollectionId && !!runConfiguration.datePublished && !project.isReadOnly;
      const showProviderHub = shouldShowAdequacyTab
        && runConfiguration.isLatestPublished
        && !runConfiguration.isTermed && !runConfiguration.isProjected && !runConfiguration.isProjected
        && !this.props.isDualMembershipMode
        && (!this.props.compareToRunConfiguration)
        && this.props.enabledFeatureFlags.has(Features.PROVIDER_HUB);
      const showNetworkIntegrity = runConfiguration
        && (runConfiguration.status === ProjectRunConfigurationStatus.Succeeded)
        && this.props.project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId == this.props.runConfiguration.projectRunConfigurationId)?.showNetworkIntegrityDashboard
        && !(regionFilters.state
          || regionFilters.band
          || regionFilters.county
          || regionFilters.inspection
          || regionFilters.specialty)
        && this.props.enabledFeatureFlags.has(Features.ENABLE_NETWORK_INTEGRITY_DASHBOARD);

      const statuses = (!loading || null) && (this.state.statusBanners);

      const olderRunConfiguration = !!this.props.compareToRunConfiguration ?
        this.props.compareToRunConfiguration.dateCreated > runConfiguration.dateCreated ?
          runConfiguration :
          this.props.compareToRunConfiguration :
        runConfiguration;

      const newerRunConfiguration = !!this.props.compareToRunConfiguration && !this.props.isDualMembershipMode ?
        olderRunConfiguration === runConfiguration ?
          this.props.compareToRunConfiguration :
          runConfiguration :
        undefined;

      if (haveAccess) {
        return (
          <>
            <Page documentTitle={project?.resource.name || "Loading"}>
              <Breadcrumbs template={BreadcrumbTemplateEnum.NetworkAssessment} items={[project.resource.name]} />
              <PageInner>
                <PageHeader title={project.resource.name} disableHR={true}>
                  <PageHeaderLeft>
                    <span className="btn-group">
                      <ResourceControls
                        defaultProjectOptionsOpen={showProjectOptions}
                        resourceId={project.projectId}
                        isArchived={project.resource.isArchived}
                        isProjectRunning={project.isRunning}
                        isReadOnly={project.isReadOnly}
                        isShared={project.resource.isShared}
                        projectName={project.resource.name}
                      />
                    </span>
                  </PageHeaderLeft>
                </PageHeader>
                <PageSection>
                  <ViewableFieldsContainer projectRunConfigurationId={runConfiguration ? runConfiguration.projectRunConfigurationId : null} customProjectId={project.projectId} specialtyGroupCode={this.props.filters.find(f => f.filterType == FilterType.Specialty)?.filterValue ?? ""}>
                    <div>
                      <div className={classNames("row", { mt20: !runConfiguration }, "project-status-container")} ref={this.statusContainerRef}>
                        <div className="col-12">
                          {statuses}
                          {user.showMaintenanceAlert &&
                            <MaintenanceAlert
                              maintenanceTime={user.maintenanceTime}
                            />
                          }
                        </div>
                      </div>
                      {this.state.filters}
                      {runConfiguration?.status !== ProjectRunConfigurationStatus.Canceled && (
                        <div className="row">
                          <div className="col-12">
                            <div className="tabs-style-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <ul className="nav nav-tabs" role="menu">
                                  {showNetworkIntegrity && (
                                    <li className="nav-item" role="none"><a className={this.state.activeProjectModule === "Network-Integrity" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => { this.setActiveProjectModule("Network-Integrity"); })}>Network Integrity</a></li>
                                  )}
                                  {shouldShowAccuracyTab && (
                                    <li className="nav-item" role="none"><a className={this.state.activeProjectModule === "Accuracy" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => { this.setActiveProjectModule("Accuracy"); this.setState((prevState) => ({ ...prevState, showTrendChart: true })) })}>Accuracy</a></li>
                                  )}
                                  {(shouldShowAdequacyTab || null) && (
                                    <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Adequacy" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Adequacy"))}>Adequacy</a></li>
                                  )}
                                  {(showSummary || null) && (
                                    <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Summary" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Summary"))}>Summaries</a></li>
                                  )}
                                  {(showMapping || null) && (
                                    <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Mapping" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Mapping"))}>Maps</a></li>
                                  )}
                                  {(showProviderHub || null) && (
                                    <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Provider-Hub" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Provider-Hub"))}>Provider Hub</a></li>
                                  )}
                                  {showPciData && (
                                    <React.Fragment>
                                      <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Claims-Activity" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Claims-Activity"))}>Provider Claims Activity</a></li>
                                      <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Claims-Insights" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Claims-Insights"))}>Effectiveness and Efficiency</a></li>
                                    </React.Fragment>
                                  )}
                                </ul>
                                {(this.state.activeProjectModule !== "Mapping" && this.state.activeProjectModule !== "Claims-Activity" && this.state.activeProjectModule !== "Claims-Insights" && this.state.activeProjectModule !== "Provider-Hub") && (
                                  <div className="d-flex flex-column">
                                    {runConfiguration?.timeDistanceCalculatorType && <p className="mb-2">{!!newerRunConfiguration && getTimeString(olderRunConfiguration, this.props.runFrequency)} Distance Method: <b>{timeDistanceCalculatorTypeDisplayMap[olderRunConfiguration?.timeDistanceCalculatorType]}</b></p>}
                                    {!!newerRunConfiguration && <p className="mb-0">{getTimeString(newerRunConfiguration, this.props.runFrequency)} Distance Method: <b>{timeDistanceCalculatorTypeDisplayMap[newerRunConfiguration?.timeDistanceCalculatorType]}</b></p>}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="container-fluid px-0">
                              {this.state.activeProjectModule === "Adequacy" && this.props.project.showAccuracyGapAnalysis && this.props.runConfiguration && this.props.runConfiguration.showAccuracyGapAnalysis &&
                                (this.renderAgaStatus(user, this.props))}
                              {(this.state.activeProjectModule !== "Summary" && this.state.activeProjectModule !== "Mapping" && this.state.activeProjectModule !== "Claims-Activity" && this.state.activeProjectModule !== "Claims-Insights" && this.state.activeProjectModule !== "Provider-Hub" && this.state.activeProjectModule !== "Network-Integrity") && (
                                <>
                                  <Options
                                    disabled={loading || project.isRunning}
                                    showGraphicalArea={this.state.showCharts}
                                    onShowGraphicalAreaClick={this.handleShowCharts}
                                    defaultProjectOptionsOpen={showProjectOptions}
                                    isReadOnly={project.isReadOnly || project.isEditRestricted}
                                    hasProspectOrMarketProviders={this.props.hasProspectOrMarketProviders}
                                    onDisplayTrendChartChange={this.onDisplayTrendChartChange}
                                    showTrendChart={this.state.showTrendChart}
                                    showTrendChartToggle={this.state.showCharts && this.state.activeProjectModule === "Adequacy"}
                                  />
                                  {(this.state.showCharts || null) &&
                                    <>
                                      {this.state.showTrendChart && (
                                        <Charts {...this.props} module={this.state.activeProjectModule} />
                                      )}
                                      {(shouldShowAdequacyTab && !this.props.isDualMembershipMode || null) && (
                                        //<Maps {...this.props} />
                                        <div className={this.state.showTrendChart ? "qes-leaflet-concealer" : undefined} aria-hidden={this.state.showTrendChart}>
                                          <CountyGapsMap {...this.props} />
                                        </div>
                                      )}
                                    </>
                                  }
                                </>
                              )}

                              <div className="row">
                                <div className="col-12">
                                  <div className="tabs-style-2">
                                    <div className="tab-content overview-tab-content">
                                      <Switch>
                                        <Route path={`${this.props.match.path}/accuracy`} component={AccuracyModule} />
                                        <Route path={`${this.props.match.path}/adequacy`} component={AdequacyModule} />
                                        <Route path={`${this.props.match.path}/summary`} component={SummaryRouter} />
                                        {
                                          this.props.enabledFeatureFlags.has(Features.PROVIDER_MEMBER_MAPS_V2) ? (
                                            <Route path={`${this.props.match.path}/mapping`}
                                                   render={(routeProps) => (
                                                    <NetworkCoverageMap
                                                      {...routeProps}
                                                      processProviderChanges={this.props.processProviderChanges}
                                                      setProjectHasUnprocessedProjectionProviders={this.props.setProjectHasUnprocessedProjectionProviders}
                                                      />
                                                    )} 
                                           />
                                          ) : (
                                            <Route path={`${this.props.match.path}/mapping`} component={MappingModule} />
                                          )
                                        }
                                        <Route path={`${this.props.match.path}/provider-hub`} component={ProviderHubLandingPage} />
                                        <Route
                                          path={`${this.props.match.path}/claims-activity`}
                                          render={(props: RouteComponentProps) => (
                                            <ClaimsActivityRouter {...props} />
                                          )}
                                        />
                                        <Route
                                          path={`${this.props.match.path}/claims-insights`}
                                          render={(props: RouteComponentProps) => (
                                            <ClaimsInsightsRouter {...props} />
                                          )}
                                        />
                                        <Route
                                          path={`${this.props.match.path}/${this.networkIntegrityModulePath}`}
                                          component={NetworkIntegrityPage}
                                        />
                                      </Switch>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </ViewableFieldsContainer>
                </PageSection>
              </PageInner>
            </Page>
          </>
        );
      } else {
        //TODO: Maybe we can push '/' to the router at this point?
        return null;
      }
    } else if (!error) {
      return (
        <SkeletonOverview perspective={perspective} />
      );
    } else {
      // caught by error boundary
      throw { status: error.status, statusText: error.statusText, traceId: error.responseJSON?.traceId };
    }
  }

  private renderAgaStatus(user: UserInfo, props: CountyProjectOverviewProps): JSX.Element {
    if (!this.isUserAuthorizedOnAccuracyGapAnalysis(user)) return null;
    switch (this.props.runConfiguration.accuracyGapAnalysisStatus) {
      case AccuracyGapAnalysisStatus.Succeeded:
        return (
          <AgaAlert
            compareToRunConfiguration={this.props.compareToRunConfiguration}
            isDualMembershipMode={this.props.isDualMembershipMode}
            primaryConfiguration={this.props.primaryConfiguration}
            projectId={this.props.project.projectId}
            runConfiguration={this.props.runConfiguration}
            supplementaryConfiguration={this.props.supplementaryConfiguration}
            userRoles={user.roles}
          />
        );
      case AccuracyGapAnalysisStatus.Failed:
      case AccuracyGapAnalysisStatus.Canceled:
        return (
          <QESAlert type="danger" key="agaFailed" persist={true}>
            <strong>Warning!</strong> Accuracy Gap Analysis Failed.
            {this.props.enabledFeatureFlags.has(Features.USE_AGA_PARENT_ORCHESTRATION) &&
              <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
                <QESButton
                  className="ml-3"
                  disabled={this.state.disableAgaRunBanner}
                  onClick={() => {
                    this.setState({ disableAgaRunBanner: true });
                    this.props.rerunAccuracyGapAnalysis(props.project.projectId, props.runConfiguration.projectRunConfigurationId)
                  }}
                  qesTestId="project-overview__run-project--aga-failed"
                  qesType="secondary"
                >
                  <QesIcon iconName={QesIcons.FontAwesome.Rocket} isDecorative />
                  Run Again
                </QESButton>
              </RoleRestricted>
            }
          </QESAlert>
        );

      case AccuracyGapAnalysisStatus.Queued:
      case AccuracyGapAnalysisStatus.Analyzing:
        return (
          <QESAlert type="info" key="readonly" classNames="mt-3">
            Accuracy gap analysis is calculating.
          </QESAlert>
        );

      case AccuracyGapAnalysisStatus.Unknown:
      default:
        return null;
    }
  }

  private isUserAuthorizedOnAccuracyGapAnalysis(user: UserInfo): boolean {
    return (user.roles.includes(Roles.ACCURACY_LICENSE_TIER_1) || user.roles.includes(Roles.ACCURACY_LICENSE_TIER_2))
  }

  private renderFilters(filters: RegionFilterItem[], removeFilter: (urlPart: string) => void) {
    if (!filters || filters.length === 0) {
      return null;
    }
    const filterEls: JSX.Element[] = filters.map((filter: RegionFilterItem, i: number) => {
      return (
        <span key={i} className={`overview-filter`} onClick={() => removeFilter(filter.urlPart)}>
          <span className="mr10">{filter.displayText}</span>
          <QesIcon iconName={QesIcons.FontAwesome.TimesCircle} description="remove this filter" width="12" />
        </span>
      );
    });

    return (
      <div className="row">
        <div className="col-12">
          <div className="overview-filters">
            <strong className="mr15">Current Filter:</strong>
            {filterEls}
          </div>
        </div>
      </div>
    );
  }

  private async renderStatus(
    user: UserInfo,
    isShared: boolean,
    isProjectRunning: boolean,
    processProviderChanges: (reprocessFailure: boolean) => Promise<void>,
    project: ProjectOptions,
    runConfiguration: ProjectRunConfigurationResult,
    runProject: () => void,
    requeueMarketAndProspectNCP: () => void,
    onPublished: () => void,
    isReadOnly: boolean,
    isEditRestricted: boolean,
  ) {
    const termAdmin = user.roles.indexOf(Roles.TERM_PROVIDER_ADMIN) > -1;
    const projectionProcessor = user.roles.indexOf(Roles.PROJECTION_PROCESSOR) > -1;
    const exceptionManager = user.roles.indexOf(Roles.EXCEPTION_MANAGER) > - 1;
    const showTermMessage = termAdmin && project.hasUnprocessedProviderTerminations && !project.isRunning && runConfiguration && runConfiguration.status !== ProjectRunConfigurationStatus.Failed;
    const showSpecialtyGroupExceptionMessage = exceptionManager && project.hasUnprocessedSpecialtyGroupExceptions && !project.isRunning && runConfiguration && runConfiguration.status !== ProjectRunConfigurationStatus.Failed;

    //don't show target message for accuracy-only projects
    const showProjectionMessage = project.hasUnprocessedProjectionProviders && projectionProcessor && !project.isRunning && runConfiguration && runConfiguration.status !== ProjectRunConfigurationStatus.Failed;
    const renderUnprocessedProvidersSidePanelLinkElement = this.renderUnprocessedProvidersSidePanelLink(projectionProcessor, termAdmin, project.projectId, processProviderChanges);
    const renderUnprocessedSpecialtyGroupExceptionsSidePanelLinkElement = this.renderUnprocessedSpecialtyGroupExceptionsChangesSidePanelLinkElement(project.projectId);

    if (isReadOnly) {
      if (!project.isRunning && runConfiguration && runConfiguration.status === ProjectRunConfigurationStatus.FailedProviderChanges) {
        return (
          <QESAlert type="danger" persist={true}>
            {isShared
              ? <>An error occurred during processing. We apologize for the inconvenience. Please contact your administrator.</>
              : <>Project failed while processing provider changes. If you are not sure you should reprocess this project, submit a ticket to QES Intake.
                <QESButton className="ml-3" qesType="secondary" onClick={() => processProviderChanges(true)} qesTestId="project-overview__process-provider-changes">
                  <QesIcon iconName={QesIcons.FontAwesome.Rocket} width="14" height="16" svgStyle={{ paddingBottom: "2px" }} isDecorative />Re-Process Provider Changes
                </QESButton>
              </>}
          </QESAlert>
        );
      }
      if (!project.isRunning && runConfiguration && runConfiguration.status === ProjectRunConfigurationStatus.FailedProspectMarketNcp) {
        return (
          <QESAlert type="danger" key="ncpFailed" persist={true}>
            <strong>Warning!</strong> Processing next closest providers failed.
            <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
              <QESButton className="ml-3" qesType="secondary" onClick={requeueMarketAndProspectNCP} qesTestId="project-overview__requeue-impact-ncp--impact-ncp-failed--readonly">
                <QesIcon iconName={QesIcons.FontAwesome.Rocket} width="14" height="16" svgStyle={{ paddingBottom: "2px" }} isDecorative /> Run Again
              </QESButton>
            </RoleRestricted>
          </QESAlert>
        );
      }
      return (
        <QESAlert type="info" key="readonly" persist={true}>
          This project is in a read-only mode while processing updates. Some items may not appear on the trend chart while they are being worked on.
        </QESAlert>
      );
    }
    if (isEditRestricted) {
      const multiMessage: JSX.Element[] = [];
      if (!project.isRunning && runConfiguration && runConfiguration.status === ProjectRunConfigurationStatus.FailedProspectMarketNcp) {
        multiMessage.push(
          <QESAlert type="danger" key="ncpFailed" persist={true}>
            <strong>Warning!</strong> Processing next closest providers failed.
            <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
              <QESButton className="ml-3" qesType="secondary" onClick={requeueMarketAndProspectNCP} qesTestId="project-overview__requeue-impact-ncp--impact-ncp-failed--edit-restricted">
                <QesIcon iconName={QesIcons.FontAwesome.Rocket} width="14" height="16" svgStyle={{ paddingBottom: "2px" }} isDecorative /> Run Again
              </QESButton>
            </RoleRestricted>
          </QESAlert>
        );
      } else {
        multiMessage.push(
          <QESAlert type="info" key="editrestricted" persist={true}>
            {`This project is in a read-only mode while processing Next Closest Prospect and Market Providers.`}
          </QESAlert>
        );
      }

      if (!isShared) {
        // build the not published banner and add it to the multimessage
        if (project.canPublish && runConfiguration && runConfiguration.datePublished === undefined) {
          multiMessage.push((
            <QESAlert type="warning" key="projectUnpublished" persist={true}>
              <strong>Warning!</strong> Your project results are unpublished.
              <FlyoutLink
                classNames="ml-3"
                content={{
                  body: this.getPublishSidePanel(project, runConfiguration, onPublished),
                  title: "Publish Options",
                }}
                testId="publish-project-flyout-link"
              >
                <QesIcon iconName={QesIcons.FontAwesome.ThumbsOutlineUp} isDecorative svgStyle={{ paddingBottom: "2px" }} classNames="mr-1" />
                Publish
              </FlyoutLink>
            </QESAlert>
          ));
        }
      }

      if (multiMessage.length <= 1) {
        return multiMessage;
      }

      return (
        <QESAlertsContainer containerClass="mb-3">
          {multiMessage}
        </QESAlertsContainer>
      );
    }
    if (isShared) {
      if (runConfiguration) {
        const indeterminateStatusMessage = this.renderIndeterminateStatusMessage(project, runConfiguration.status);
        if (indeterminateStatusMessage) {
          return indeterminateStatusMessage;
        }

        const multiMessage: JSX.Element[] = [];
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.QueuedProviderChanges) {
          multiMessage.push(
            <QESAlert key="queued-changes" type="info" persist={true}>
              Queued to Process Provider Changes.
            </QESAlert>
          );
        }
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.ProcessingProviderChanges) {
          multiMessage.push(
            <QESAlert key="processing-changes" type="info" persist={true}>
              Processing Provider Changes.
            </QESAlert>
          );
        }
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.QueuedProspectMarketNcp) {
          multiMessage.push(
            <QESAlert key="queued-ncp" type="info" persist={true}>
              Queued to process Next Closest Providers.
            </QESAlert>
          );
        }
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.ProcessingProspectMarketNcp) {
          multiMessage.push(
            <QESAlert key="processing-ncp" type="info" persist={true}>
              Processing Next Closest Providers.
            </QESAlert>
          );
        }
        if (runConfiguration.status === ProjectRunConfigurationStatus.FailedProviderChanges) {
          multiMessage.push(
            <QESAlert key="failed" type="info" persist={true}>
              An error occurred during processing. We apologize for the inconvenience. Please contact your administrator.
            </QESAlert>
          );
        }
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.QueuedSpecialtyGroupExceptionAnalysis) {
          multiMessage.push(
            <QESAlert key="queued-exceptions" type="info" persist={true}>
              Queued to Recalculate Results for Specialty Group Exception Change.
            </QESAlert>
          );
        }
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.ProcessingSpecialtyGroupExceptionAnalysis) {
          multiMessage.push(
            <QESAlert key="recalculating-exceptions" type="info" persist={true}>
              Recalculating Results for Specialty Group Exception Change.
            </QESAlert>
          );
        }
        if (showSpecialtyGroupExceptionMessage) {
          multiMessage.push(
            <RoleRestricted key="unprocessed-exceptions" allowedRoles={[Roles.EXCEPTION_MANAGER]}>
              <QESAlert type="warning" persist={true}>
                <strong> Warning!</strong> Your project has unprocessed specialty group exceptions.
                {renderUnprocessedSpecialtyGroupExceptionsSidePanelLinkElement}
              </QESAlert>
            </RoleRestricted>);
        }
        if (showTermMessage && showProjectionMessage) {
          multiMessage.push(
            <RoleRestricted key="unprocessed-terms-projections" allowedRoles={[Roles.PROJECTION_PROCESSOR]}>
              <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
                <QESAlert type="warning" persist={true}>
                  <strong>Warning!</strong> Your project has unprocessed provider terminations and unprocessed targeted or excluded providers.
                  {renderUnprocessedProvidersSidePanelLinkElement}
                </QESAlert>
              </RoleRestricted>
            </RoleRestricted>
          );
        } else if (showTermMessage) {
          multiMessage.push(
            <RoleRestricted key="unprocessed-terms" allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
              <QESAlert type="warning" persist={true}>
                <strong>Warning!</strong> Your project has unprocessed provider terminations.
                {renderUnprocessedProvidersSidePanelLinkElement}
              </QESAlert>
            </RoleRestricted>
          );
        } else if (showProjectionMessage) {
          multiMessage.push(
            <RoleRestricted key="unprocessed-projections" allowedRoles={[Roles.PROJECTION_PROCESSOR]}>
              <QESAlert type="warning" persist={true}>
                <strong>Warning!</strong> Your project has unprocessed targeted or excluded providers.
                {renderUnprocessedProvidersSidePanelLinkElement}
              </QESAlert>
            </RoleRestricted>
          );
        }
        if (multiMessage.length <= 1) {
          return multiMessage;
        } else if (multiMessage.length > 1) {
          return (
            <QESAlertsContainer containerClass="mb-3">
              {multiMessage}
            </QESAlertsContainer>
          );
        }
      }
    } else {
      if (!runConfiguration) {
        return (
          <QESAlert type="info" persist={true}>
            <strong>Thank you!</strong> Your project has been created, please run it.
            <QESButton className="ml-3" qesType="secondary" onClick={runProject} qesTestId="project-overview__run-project--no-config">
              <QesIcon iconName={QesIcons.FontAwesome.Rocket} width="14" height="16" svgStyle={{ paddingBottom: "2px" }} isDecorative /> Run Now
            </QESButton>
          </QESAlert>
        );
      }

      // check for indeterminate state first and bail if it's in that state
      const indeterminateStatusMessage = this.renderIndeterminateStatusMessage(project, runConfiguration.status);
      if (indeterminateStatusMessage) {
        return indeterminateStatusMessage;
      }

      const multiMessage: JSX.Element[] = [];

      switch (runConfiguration.status) {
        case ProjectRunConfigurationStatus.Created:
          multiMessage.push((
            <QESAlert key="statusbanner" type="info" persist={true}>
              Your project has been created, is valid, and is ready to run.
              <QESButton className="ml-3" qesType="secondary" onClick={runProject} qesTestId="project-overview__run-project--status-created">
                <QesIcon iconName={QesIcons.FontAwesome.Rocket} width="14" height="16" svgStyle={{ paddingBottom: "2px" }} isDecorative /> Run Now
              </QESButton>
            </QESAlert>
          ));
          break;
        case ProjectRunConfigurationStatus.Queued:
          multiMessage.push((
            <QESAlert type="info" key="statusbanner" persist={true}>
              Your project is currently queued to be run.
            </QESAlert>
          ));
          break;
        case ProjectRunConfigurationStatus.Analyzing:
        case ProjectRunConfigurationStatus.Aggregating:
          multiMessage.push((
            <QESAlert key="statusbanner" type="info" persist={true}>
              Your project is currently running.
            </QESAlert>
          ));
          break;
        // case ProjectRunConfigurationStatus.Canceled:
        // this was moved to a conditional below
        // break;
        case ProjectRunConfigurationStatus.Failed:
          multiMessage.push((
            <QESAlert type="warning" key="projectFailed" persist={true}>
              <strong>Warning!</strong> Your latest project run failed.
              <QESButton className="ml-3" qesType="secondary" onClick={runProject} qesTestId="project-overview__run-project--run-failed">
                <QesIcon iconName={QesIcons.FontAwesome.Rocket} width="14" height="16" svgStyle={{ paddingBottom: "2px" }} isDecorative /> Run Again
              </QESButton>
            </QESAlert>
          ));
          break;
        case ProjectRunConfigurationStatus.FailedProspectMarketNcp:
          multiMessage.push((
            <QESAlert type="danger" key="ncpFailed" persist={true}>
              <strong>Warning!</strong> Processing next closest providers failed.
              <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
                <QESButton className="ml-3" qesType="secondary" onClick={requeueMarketAndProspectNCP} qesTestId="project-overview__requeue-impact-ncp--config-status-failed-impact-ncp">
                  <QesIcon iconName={QesIcons.FontAwesome.Rocket} width="14" height="16" svgStyle={{ paddingBottom: "2px" }} isDecorative /> Run Again
                </QESButton>
              </RoleRestricted>
            </QESAlert>
          ))
          break;
        case ProjectRunConfigurationStatus.FailedProviderChanges:
          multiMessage.push((
            <QESAlert type="warning" key="providerChangesFiled" persist={true}>
              <strong>Warning!</strong> Processing provider changes failed.
            </QESAlert>
          ));
          break;
        case ProjectRunConfigurationStatus.Succeeded:
          // build the project change reason list and add it to the multimessage
          if (project.projectNeedsFullRun) {
            const list = project.projectChangeReasons.map((r) => <li key={r}>{translateProjectChangeReason(r)}</li>);
            multiMessage.push((
              <QESAlert
                collapseTitle={
                  <>
                    <strong>Warning!</strong> Your project results are out of date.
                    {/* HTML Syntax doesn't allow a button inside a button (QESAlert's collapse button),
                      so we get around it here with a span with role="button" and the properties that otherwise would
                      be native to a button (onClick, onKeyDown, tabIndex) 
                      See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role for more
                  */}
                    <span
                      className="d-inline-block ml-3"
                      onClick={runProject}
                      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); runProject(); } }}
                      role="button"
                      tabIndex={0}
                      data-testid="project-overview__run-project--results-out-of-date"
                    >
                      <QesIcon iconName={QesIcons.FontAwesome.Rocket} width="14" height="16" svgStyle={{ paddingBottom: "2px" }} isDecorative /> Run Now
                    </span>
                  </>
                }
                key="projectChanged"
                persist={true}
                type="warning"
              >
                <p className="pt5">The following changes have occurred since your project was last run:</p>
                <ul>
                  {list}
                </ul>
              </QESAlert>
            ));
          }

          if (project.showMotiveAppropriatenessScore &&
            !!project.templateId &&
            !!runConfiguration.adequacyTemplateCollectionId) {
            const templateInfo = await this.templateService.getTemplateById(runConfiguration.adequacyTemplateCollectionId, project.templateId, false);

            if (templateInfo.motiveCrosswalkCodeMapSetId === undefined || templateInfo.motiveCrosswalkCodeMapSetId === null) {
              multiMessage.push((
                <QESAlert type="warning" key="motiveReqd" persist={true}>
                  <strong>Warning!</strong> Motive data will not be available based on template configuration.
                </QESAlert>
              ));
            }
          }

          // build the not published banner and add it to the multimessage
          if (project.canPublish && runConfiguration && runConfiguration.datePublished === undefined) {
            multiMessage.push((
              <QESAlert type="warning" key="projectUnpublished" persist={true}>
                <strong>Warning!</strong> Your project results are unpublished.
                <FlyoutLink
                  classNames="ml-3"
                  content={{
                    body: this.getPublishSidePanel(project, runConfiguration, onPublished),
                    title: "Publish Options",
                  }}
                  testId="publish-project-flyout-link"
                  size="40"
                >
                  <QesIcon iconName={QesIcons.FontAwesome.ThumbsOutlineUp} isDecorative svgStyle={{ paddingBottom: "2px" }} classNames="mr-1" />
                  Publish
                </FlyoutLink>
              </QESAlert>
            ));
          }
          break;
      }

      if (project.projectRunConfigurations.some((prc) => (prc.status === ProjectRunConfigurationStatus.Canceled && new Date(prc.dateCreated).getTime() === Math.max(...project.projectRunConfigurations.map((c) => new Date(c.dateCreated).getTime()))))) {
        let message = "Your latest project run was canceled.";
        if (runConfiguration.status === ProjectRunConfigurationStatus.Succeeded) {
          message += ` The data below is from your ${formatDateTime(runConfiguration.dateCreated)} run.`;
        } else if (project.projectRunConfigurations.filter((prc) => prc.status === ProjectRunConfigurationStatus.Succeeded).length === 0) {
          message += " This project has no successful runs to pull data from.";
        }
        multiMessage.push((
          <QESAlert type="warning" key="projectCancelled" persist={true}>
            <strong>Warning!</strong> {message}
            <QESButton className="ml-3" qesType="secondary" onClick={runProject} qesTestId="project-overview__run-project--run-canceled">
              <QesIcon iconName={QesIcons.FontAwesome.Rocket} width="14" height="16" svgStyle={{ paddingBottom: "2px" }} isDecorative /> Run Again
            </QESButton>
          </QESAlert>
        ));
      }

      if (showTermMessage && showProjectionMessage) {
        multiMessage.push(
          <RoleRestricted allowedRoles={[Roles.PROJECTION_PROCESSOR]} key="unprocessedTargetsAndTerminations">
            <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
              <QESAlert type="warning" persist={true}>
                <strong>Warning!</strong> Your project has unprocessed provider terminations and unprocessed targeted or excluded providers.
                {renderUnprocessedProvidersSidePanelLinkElement}
              </QESAlert>
            </RoleRestricted>
          </RoleRestricted>,
        );
      } else if (showTermMessage) {
        multiMessage.push(
          <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]} key="unprocessedTerminations">
            <QESAlert type="warning" persist={true}>
              <strong>Warning!</strong> Your project has unprocessed provider terminations.
              {renderUnprocessedProvidersSidePanelLinkElement}
            </QESAlert>
          </RoleRestricted>,
        );
      } else if (showProjectionMessage) {
        multiMessage.push(
          <RoleRestricted allowedRoles={[Roles.PROJECTION_PROCESSOR]} key="unprocessedTargets">
            <QESAlert type="warning" persist={true}>
              <strong>Warning!</strong> Your project has unprocessed targeted or excluded providers.
              {renderUnprocessedProvidersSidePanelLinkElement}
            </QESAlert>
          </RoleRestricted>,
        );
      }
      if (showSpecialtyGroupExceptionMessage) {
        multiMessage.push(
          <RoleRestricted allowedRoles={[Roles.EXCEPTION_MANAGER]} key="unproccessedSpecialtyGroupExceptions">
            <QESAlert type="warning" persist={true}>
              <strong> Warning!</strong> Your project has unprocessed specialty group exceptions.
              {renderUnprocessedSpecialtyGroupExceptionsSidePanelLinkElement}
            </QESAlert>
          </RoleRestricted>);
      }

      if (multiMessage.length <= 1) {
        return multiMessage;
      }

      return (
        <QESAlertsContainer containerClass="mb-3">
          {multiMessage}
        </QESAlertsContainer>
      );
    }

    return null;
  }

  private renderUnprocessedProvidersSidePanelLink = (getTargets: boolean, getTerminations: boolean, projectId: number, processProviderChanges: (reprocessFailure: boolean) => Promise<void>) => {
    return (
      <PendingProviderChangesSidePanelLink
        getTargets={getTargets}
        getTerminations={getTerminations}
        processProviderChanges={processProviderChanges}
        projectId={projectId}
        resetStatusBanners={this.resetStatusBanners}
        isZipProject={false}
      />
    );
  }

  private renderUnprocessedSpecialtyGroupExceptionsChangesSidePanelLinkElement = (projectId: number) => {
    return (<PendingUnprocessedSpecialtyGroupExceptionsChangesSidePanelLink
      projectId={projectId}
    />);
  }

  private renderIndeterminateStatusMessage(project: ProjectOptions, runConfigurationStatus: ProjectRunConfigurationStatus) {
    if (runConfigurationStatus === ProjectRunConfigurationStatus.Unknown || !project || !project.resource) {
      return (
        <QESAlert type="warning" persist={true}>
          <strong>Uh oh!</strong> Something might be wrong with your project. It&apos;s in an indeterminate state.
        </QESAlert>
      );
    }

    return null;
  }

  private getPublishSidePanel = (project: ProjectOptions, runConfiguration: ProjectRunConfiguration, onPublished: () => void) => {
    return (
      <Publish
        project={project}
        runConfigurationId={runConfiguration.projectRunConfigurationId}
        agaStatus={runConfiguration.accuracyGapAnalysisStatus}
        onPublished={onPublished}
      />
    );
  }

  private setActiveProjectModule = (projectModule: ProjectModule, replace = false) => {
    let newPath = "";

    if (projectModule === "Summary" || projectModule === "Mapping" || projectModule === "Claims-Activity" || projectModule === "Claims-Insights" || projectModule === "Provider-Hub" || projectModule === "Network-Integrity") {
      newPath = this.getNewPath(this.props.location, projectModule.toLowerCase(), true);
    } else {
      newPath = this.getNewPath(this.props.location, projectModule.toLowerCase());
    }

    if (replace) {
      this.props.history.replace(newPath);
    } else {
      this.props.history.push(newPath);
    }

    this.setState({
      activeProjectModule: projectModule,
    });
  }

  private handleShowCharts = () => {
    this.setState((prevState) => ({
      ...prevState,
      showCharts: !prevState.showCharts,
    }));
  }

  private onDisplayTrendChartChange = () => {
    this.setState((prevState) => ({
      ...prevState,
      showTrendChart: !prevState.showTrendChart,
    }))
  }
}

function SkeletonOverview(props: { error?: JQuery.jqXHR, perspective?: ProjectPerspective }): JSX.Element {
  let alert: JSX.Element = null;

  if (props.error) {
    // Hopefully we'll eventually get the Http status code on the error
    // and be able to do a switch on the error code here to display a
    // more accurate message
    alert = (
      <div className="row mt20">
        <div className="col-12">
          <QESAlert type="danger" persist={true}>
            <strong>Uh oh! </strong>An unexpected error occurred. If the issue persists, please contact your administrator.
          </QESAlert>
        </div>
      </div>
    );

    throw props.error;
  }

  return (
    <Page documentTitle="Loading">
      <PageInner>
        <PageSection>
          <div className={classNames("skeleton", { shimmer: !props.error })}>
            <SkeletonHeader />

            {alert}

            <div className="row">
              <div className="col-12" />
            </div>
            <div className="row">
              <div className="col-12">
                <div className="tabs-style-2">
                  <ul className="nav nav-tabs">
                    <li className="nav-item">
                      <a className="nav-link" href="#" onClick={preventDefaultMouseEvent} role="tab">
                        <div className="bone">Accuracy</div>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a className="nav-link" href="#" onClick={preventDefaultMouseEvent} role="tab">
                        <div className="bone">Adequacy</div>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a className="nav-link" href="#" onClick={preventDefaultMouseEvent} role="tab">
                        <div className="bone">Summary</div>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a className="nav-link" href="#" onClick={preventDefaultMouseEvent} role="tab">
                        <div className="bone">Mapping</div>
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="container-fluid">
                  <div className="row ptb15 overview-options">
                    <div className="col-12">
                      <div className="pull-left bone"><QesIcon iconName={QesIcons.FontAwesome.PencilSquareOutline} isDecorative /> Project&nbsp;Options</div>
                      <div className="pull-right d-none d-sm-block">
                        <span className="bone">
                          <QesIcon iconName={QesIcons.FontAwesome.Sliders} isDecorative /> Display&nbsp;Options
                        </span>
                        <SkeletonIconToggle className="ml10-imp pl10-imp vertical-separator">
                          <QesIcon iconName={QesIcons.FontAwesome.BarChart} isDecorative />
                        </SkeletonIconToggle>
                      </div>
                      <div className="clearfix" />
                    </div>
                  </div>
                  <SkeletonCharts />
                  <div className="row">
                    {props.perspective !== ProjectPerspective.All && (< div className="col">
                      <div className="tabs-style-2">
                        <div className="tab-content overview-tab-content">
                          <div className="tab-pane in active">
                            <div className="details-filters d-none d-md-block">
                              <div>
                                <div className="btn filter-button bone">&nbsp;</div>
                              </div>
                              <div>
                                <SkeletonIconToggle text="All" />
                                <SkeletonIconToggle text="Gaps" />
                                <SkeletonIconToggle text="Exceptions" />
                                <SkeletonIconToggle className="pl20-imp vertical-separator" text="Opportunity" />
                              </div>
                            </div>
                            <SkeletonTable />
                          </div>
                        </div>
                      </div>
                    </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
}
